'use client'

// This class allows you to have custom actions on the graphic preview depending where it is.
// For example, on the graphics page, it exports the graphic when the user clicks the save button.
// On the copilot page, it adds it to chat, etc...

import { useGraphic } from '@/lib/features/graphic/GraphicContext'
import { createContext, ReactNode, useCallback, useContext } from 'react'
import { Graphic } from 'sprites-types/api/v2/graphics'

interface GraphicPreviewActionsContextType {
  onSave: () => void | Promise<void>
  onDiscard: () => void | Promise<void>
}

const GraphicPreviewActionsContext = createContext<GraphicPreviewActionsContextType | undefined>(
  undefined
)

interface GraphicPreviewActionsProviderProps {
  children: ReactNode
  onGraphicSave?: (graphic: Graphic) => void | Promise<void>
  onGraphicDiscard?: () => void | Promise<void>
}

export function GraphicPreviewActionsProvider({
  children,
  onGraphicSave,
  onGraphicDiscard,
}: GraphicPreviewActionsProviderProps) {
  const { graphic } = useGraphic()

  const value = {
    onSave: useCallback(() => {
      if (graphic && onGraphicSave) {
        onGraphicSave(graphic)
      }
    }, [graphic, onGraphicSave]),
    onDiscard: useCallback(() => {
      if (onGraphicDiscard) {
        onGraphicDiscard()
      }
    }, [onGraphicDiscard]),
  }

  return (
    <GraphicPreviewActionsContext.Provider value={value}>
      {children}
    </GraphicPreviewActionsContext.Provider>
  )
}

export function useGraphicPreviewActions() {
  const context = useContext(GraphicPreviewActionsContext)
  if (!context) {
    throw new Error('useGraphicPreviewActions must be used within a GraphicPreviewActionsProvider')
  }
  return context
}
